import React, { useEffect, useState } from "react";
import Header from "../component/layout/header";
import PageHeader from "../component/layout/pageheader";
import FooterSection from "../component/layout/footer";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { SyncLoader } from "react-spinners";

const title = "Choose Our Pricing Plan";
const subtitle = "Membership Levels";

const planLayoutMap = {
  1: {
    title: "Monthly Package Free",
    desc: "Now and then per month.",
    btnText: "Buy Now",
    banifitList: [
      { icon: "icofont-check-circled", text: "View members directory" },
      { icon: "icofont-check-circled", text: "View members profile" },
      { icon: "icofont-check-circled", text: "Access group directory" },
      { icon: "icofont-check-circled", text: "Access to groups" },
      { icon: "icofont-close-circled", text: "View site activity" },
      { icon: "icofont-close-circled", text: "Send Private messages" },
      { icon: "icofont-close-circled", text: "Add media to your profile" },
    ],
  },
  6: {
    title: "6 Month Package",
    desc: "Now and then per 6 months.",
    btnText: "Buy Now",
    banifitList: [
      { icon: "icofont-check-circled", text: "View members directory" },
      { icon: "icofont-check-circled", text: "View members profile" },
      { icon: "icofont-check-circled", text: "Access group directory" },
      { icon: "icofont-check-circled", text: "Access to groups" },
      { icon: "icofont-close-circled", text: "View site activity" },
      { icon: "icofont-close-circled", text: "Send Private messages" },
      { icon: "icofont-close-circled", text: "Add media to your profile" },
    ],
  },
  12: {
    title: "Annually Package",
    desc: "Now and then per year.",
    btnText: "Buy Now",
    banifitList: [
      { icon: "icofont-check-circled", text: "View members directory" },
      { icon: "icofont-check-circled", text: "View members profile" },
      { icon: "icofont-check-circled", text: "Access group directory" },
      { icon: "icofont-check-circled", text: "Access to groups" },
      { icon: "icofont-close-circled", text: "View site activity" },
      { icon: "icofont-close-circled", text: "Send Private messages" },
      { icon: "icofont-close-circled", text: "Add media to your profile" },
    ],
  },
};

const nameToDurationKey = {
  Free: "1",
  "6 Months": "6",
  Annually: "12",
};

const PricingPlanPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const from = location.state?.from || "/";

  const [plans, setPlans] = useState([]);
  const [symbol, setSymbol] = useState("$");
  const [loading, setLoading] = useState(false); // 🔹 Loader state
  const [purchasingPlanId, setPurchasingPlanId] = useState(null); // 🔹 Track which plan is being purchased

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await axios.get(
          `${process.env.REACT_APP_MAIN_SITE_URL}/plan/list`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setPlans(res.data.data);
        setSymbol(res.data.symbol || "$");
      } catch (err) {
        console.error("Failed to fetch plans", err);
      }
    };

    fetchPlans();
  }, []);

  // const purchasePlan = async (planId) => {
  //   try {
  //     setPurchasingPlanId(planId); // Disable specific button
  //     setLoading(true); // Show full screen loader

  //     const token = localStorage.getItem("token");

  //     const response = await axios.post(
  //       `${process.env.REACT_APP_MAIN_SITE_URL}/plan/purchase`,
  //       { plan_id: planId },
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       }
  //     );

  //     toast.success(response.data.message || "Plan purchased successfully");

  //     const redirectTo = location.state?.from?.pathname || "/";
  //     const redirectState = location.state?.from?.state || {};

  //     navigate(redirectTo, { state: redirectState });

  //     if (from?.pathname === "/profile" && from?.userId) {
  //       navigate("/profile", { state: { userId: from.userId } });
  //     } else {
  //       navigate(from);
  //     }
  //   } catch (error) {
  //     toast.error(error.response?.data?.message || "Error purchasing plan");
  //     console.error("Purchase failed", error);
  //   } finally {
  //     setLoading(false);
  //     setPurchasingPlanId(null);
  //   }
  // };

  const purchasePlan = async (planId) => {
    try {
      setPurchasingPlanId(planId); // Disable the clicked button
      setLoading(true); // Show loader

      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_MAIN_SITE_URL}/plan/purchase`,
        { plan_id: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || "Plan purchased successfully");

      // Get navigation data
      const from = location.state?.from || "/";
      const groupId = location.state?.groupId || null;
      const userId = location.state?.userId || null;

      // Handle navigation
      if (from === "/group" && groupId) {
        // Go to specific group
        navigate("/group", { state: { groupId } });
      } else if (from === "/profile" && userId) {
        // Go to specific profile
        navigate("/profile", { state: { userId } });
      } else {
        // Default or previous route
        navigate(from);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error purchasing plan");
      console.error("Purchase failed", error);
    } finally {
      setLoading(false);
      setPurchasingPlanId(null);
    }
  };

  return (
    <div>
      <Header />
      <PageHeader title={"Pricing Plan"} />

      {/* 🔹 Full Screen Loader Overlay */}
      {loading && (
        <div className="fullscreen-loader">
          <SyncLoader size={12} color="#DF314D" />
        </div>
      )}

      <section className="pricing-section padding-tb">
        <div className="container">
          <div className="section-header">
            <h4 className="theme-color">{subtitle}</h4>
            <h2>{title}</h2>
          </div>
          <div className="section-wrapper mt-4">
            <div className="pricing-plan-wrapper">
              <div className="row gx-2 gy-3 justify-content-center">
                {plans.map((plan, i) => {
                  const durationKey =
                    nameToDurationKey[plan.name?.trim()] || null;
                  const layout = planLayoutMap[durationKey];
                  const priceObj = plan.prices?.[0];

                  if (!layout || !priceObj) return null;

                  return (
                    <div className="col-lg-4 col-sm-6" key={i}>
                      <div className="price-item">
                        <div className="price-item-inner">
                          <div className="price-top">
                            <h6>{layout.title}</h6>
                            <h2>
                              {symbol}
                              {priceObj.price}
                            </h2>
                            <p>{layout.desc}</p>
                          </div>
                          <div className="price-bottom">
                            <ul>
                              {layout.banifitList.map((benefit, j) => (
                                <li key={j}>
                                  <i className={benefit.icon}></i>
                                  <p>{benefit.text}</p>
                                </li>
                              ))}
                            </ul>
                            <button
                              className="purchase-btn"
                              disabled={purchasingPlanId === plan.id}
                              onClick={(e) => {
                                e.preventDefault();
                                purchasePlan(plan.id);
                              }}
                            >
                              {purchasingPlanId === plan.id
                                ? "Processing..."
                                : layout.btnText}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      <FooterSection />
    </div>
  );
};

export default PricingPlanPage;
